import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import DatePicker from 'react-datepicker';
import ReactPaginate from 'react-paginate';
import { getActiveTenders } from '../../api/tenderService';
import 'react-datepicker/dist/react-datepicker.css';

const Tenders = () => {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const searchInputRef = useRef(null);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchTenders();
  }, []);

  useEffect(() => {
    if (searchTerm && searchInputRef.current && document.activeElement !== searchInputRef.current) {
      searchInputRef.current.focus();
      const length = searchInputRef.current.value.length;
      searchInputRef.current.setSelectionRange(length, length);
    }
  }, [searchTerm]);

  const fetchTenders = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getActiveTenders();

      if (response.isSuccess && response.data) {
        setTenders(response.data.items ?? []);
      } else {
        setError('İhaleler yüklenirken bir hata oluştu');
      }
    } catch {
      setError('İhaleler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString || dateString === '0001-01-01T00:00:00' || dateString.includes('0001-01-01')) {
        return 'Tarih belirtilmemiş';
      }

      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return 'Geçersiz tarih';
      }

      return format(date, 'dd.MM.yyyy');
    } catch {
      return dateString;
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(0);
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(0);
  };

  const filteredTenders = tenders.filter((tender) => {
    const header = tender.header?.toLowerCase() ?? '';
    const department = tender.department?.toLowerCase() ?? '';
    const normalizedSearch = searchTerm.toLowerCase();
    const matchesSearch = header.includes(normalizedSearch) || department.includes(normalizedSearch);

    if (startDate && endDate) {
      const tenderDate = new Date(tender.date);
      return matchesSearch && tenderDate >= startDate && tenderDate <= endDate;
    }

    return matchesSearch;
  });

  const pageCount = Math.ceil(filteredTenders.length / itemsPerPage);
  const offset = currentPage * itemsPerPage;
  const currentTenders = filteredTenders.slice(offset, offset + itemsPerPage);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
    window.scrollTo(0, 0);
  };

  const actionBodyTemplate = (rowData) => (
    <Link
      to={`/ihaleler/${rowData.urlTag}`}
      className="btn btn-primary btn-sm"
      state={{ guid: rowData.guid }}
    >
      Detaya Git
    </Link>
  );

  if (loading) {
    return (
      <div className="council-decisions">
        <div className="container">
          <h1>İhaleler</h1>
          <div className="text-center py-5">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="council-decisions">
        <div className="container">
          <h1>İhaleler</h1>
          <div className="alert alert-danger">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="council-decisions">
      <Helmet>
        <title>İhaleler | İskenderun Belediyesi</title>
        <meta name="description" content="İskenderun Belediyesi ihaleler listesi." />
      </Helmet>

      <div className="container">
        <h1>İhaleler</h1>

        <div className="news-tabs-filter">
          <div className="news-tabs-filter-left">
            <div className="news-tabs-filter-left-item">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="İhalelerde Ara"
                value={searchTerm}
                onChange={handleSearch}
              />
              <i className="pi pi-search"></i>
            </div>
          </div>

          <div className="news-tabs-filter-right">
            <div className="news-tabs-filter-right-item">
              <DatePicker
                selected={startDate}
                onChange={handleDateChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                locale={tr}
                className="form-control"
                placeholderText="Tarih Aralığı"
                dateFormat="dd.MM.yyyy"
                isClearable
              />
              <i className="pi pi-calendar"></i>
            </div>
          </div>
        </div>

        <DataTable value={currentTenders} className="council-table">
          <Column
            field="date"
            header="Tarih"
            style={{ width: '10%' }}
            body={(rowData) => formatDate(rowData.date)}
          />
          <Column field="header" header="İhale Başlığı" style={{ width: '40%' }} />
          <Column field="department" header="Birim" style={{ width: '20%' }} />
          <Column
            field="statusName"
            header="Durum"
            style={{ width: '15%' }}
            body={(rowData) => (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: rowData.statusColorCode || '#ccc',
                    marginRight: '8px'
                  }}
                />
                {rowData.statusName}
              </div>
            )}
          />
          <Column body={actionBodyTemplate} header="" style={{ width: '15%' }} />
        </DataTable>

        <ReactPaginate
          previousLabel="<"
          nextLabel=">"
          breakLabel="..."
          pageCount={pageCount}
          forcePage={currentPage}
          marginPagesDisplayed={2}
          pageRangeDisplayed={5}
          onPageChange={handlePageClick}
          containerClassName="pagination"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          breakClassName="page-item"
          breakLinkClassName="page-link"
          activeClassName="active"
        />
      </div>
    </div>
  );
};

export default Tenders;
